import * as Sentry from '@sentry/react';

import { getConfig } from '../../config';
import type { ExtrasAndTags, LogOptions } from './types';

const DEFAULT_SENTRY_DSN =
	'https://8b156bc879d5d53284bbaab24c837162@o55978.ingest.us.sentry.io/4511232360710144';
const DEFAULT_PACKAGE_NAME = 'twg-microsite';
const DEFAULT_TEAM_NAME = 'twg-graphyte';
const DEFAULT_PRODUCT = 'twgMicrosite';
const DEFAULT_LOGGER = 'logger.default';
const ASSET_LOAD_ERROR_FINGERPRINT = 'twg-microsite-asset-load-error';
const ANONYMOUS_USER_ID_PREFIX = 'anon:';

// Messages browsers (and Atlaspack's script loader) use when a lazy JS chunk fails to load.
const CHUNK_LOAD_ERROR_MESSAGES = [
	'Failed to fetch dynamically imported module',
	'Importing a module script failed',
	'error loading dynamically imported module',
];

type AssetKind = 'stylesheet' | 'script';

type ReportableException = {
	exception: unknown;
	assetKind?: AssetKind;
};

const getEnvironment = (): string => {
	const { env } = getConfig();
	switch (env) {
		case 'production':
			return 'production';
		case 'staging':
			return 'staging';
		case 'development':
		case 'local':
		default:
			return 'dev';
	}
};

/**
 * Initialises the global Sentry client for twg-microsite.
 *
 * - Disabled in dev environments (localhost / development / local) to avoid noise.
 * - Uses `beforeSend` to only capture events that were explicitly tagged via
 *   `logException` / `logMessage`, preventing unwanted global errors from being sent.
 * - Sends default PII (e.g. IP addresses) as requested by Sentry project config.
 */
export const initSentry = (): void => {
	const environment = getEnvironment();

	Sentry.init({
		dsn: DEFAULT_SENTRY_DSN,
		environment,
		enabled: environment !== 'dev',
		integrations: [Sentry.browserTracingIntegration()],
		// Keep browser tracing without sending Sentry baggage to backend APIs.
		tracePropagationTargets: [],
		// Capture 100% of transactions for performance monitoring.
		// Revisit and lower in production once traffic baselines are known.
		tracesSampleRate: 1.0,
		beforeSend: (event) => {
			// Only filter *error* events — let non-error events (e.g. transactions
			// emitted by browserTracingIntegration) through so performance
			// monitoring data still reaches Sentry. Sentry sets `event.type` to
			// values like 'transaction' / 'replay_event' for non-error events;
			// it is `undefined` for error events.
			if (event.type === undefined && event.tags?.name === undefined) {
				// Only capture error events that were explicitly tagged via
				// logException / logMessage, to avoid unwanted global errors.
				return null;
			}
			return event;
		},
		initialScope: {
			tags: {
				packageName: DEFAULT_PACKAGE_NAME,
				teamName: DEFAULT_TEAM_NAME,
				product: DEFAULT_PRODUCT,
				logger: DEFAULT_LOGGER,
			},
		},
		release: `${DEFAULT_PACKAGE_NAME}@${process.env._PACKAGE_VERSION_ ?? 'unknown'}`,
	});
};

const setTagsFromExtras = (scope: Sentry.Scope, extrasAndTags: ExtrasAndTags): void => {
	scope.setTag('name', DEFAULT_PRODUCT);

	if (extrasAndTags.loggerName !== undefined && extrasAndTags.loggerName.length !== 0) {
		scope.setTag('logger', `logger.${extrasAndTags.loggerName}`);
	}
	if (extrasAndTags.accountId !== undefined && extrasAndTags.accountId.length !== 0) {
		scope.setUser({ id: extrasAndTags.accountId });
	}
	if (extrasAndTags.teamName !== undefined && extrasAndTags.teamName.length !== 0) {
		scope.setTag('teamName', extrasAndTags.teamName);
	}
	if (extrasAndTags.touchpointId !== undefined && extrasAndTags.touchpointId.length !== 0) {
		scope.setTag('touchpointId', extrasAndTags.touchpointId);
	}
	if (typeof extrasAndTags.traceId === 'string' && extrasAndTags.traceId.length !== 0) {
		scope.setTag('traceId', extrasAndTags.traceId);
	}
};

/**
 * Sets the Atlassian account ID on the global Sentry scope so all
 * subsequent events include the user identity.
 *
 * Call this once the user has authenticated (e.g. in UserProvider).
 */
export const setSentryUser = (accountId: string): void => {
	Sentry.setUser({ id: accountId });
};

/**
 * Identifies a logged-out visitor by their per-browser analytics anonymous id,
 * so Sentry counts each browser as a separate user. Clears the user when no
 * anonymous id is available.
 */
export const setSentryAnonymousUser = (anonymousId: string | null): void => {
	if (anonymousId === null || anonymousId === '') {
		Sentry.setUser(null);
		return;
	}
	Sentry.setUser({ id: `${ANONYMOUS_USER_ID_PREFIX}${anonymousId}` });
};

const createAssetLoadError = (assetKind: AssetKind, url: string): Error => {
	const error = new Error(
		url === '' ? `Failed to load ${assetKind}` : `Failed to load ${assetKind}: ${url}`,
	);
	error.name = 'AssetLoadError';
	return error;
};

/**
 * Lazy-loaded stylesheets reject with the raw `<link>` error `Event`, which
 * Sentry reports as an untitled `<anonymous>` issue. Converts asset-load
 * Events into readable errors and flags chunk-load failures so they can be
 * grouped and filtered.
 */
const toReportableException = (exception: unknown): ReportableException => {
	if (exception instanceof Event) {
		const { target } = exception;
		if (target instanceof HTMLLinkElement) {
			return {
				exception: createAssetLoadError('stylesheet', target.href),
				assetKind: 'stylesheet',
			};
		}
		if (target instanceof HTMLScriptElement) {
			return { exception: createAssetLoadError('script', target.src), assetKind: 'script' };
		}
		return { exception: new Error(`Unhandled ${exception.type} event`) };
	}

	if (
		exception instanceof TypeError &&
		CHUNK_LOAD_ERROR_MESSAGES.some((message) => exception.message.startsWith(message))
	) {
		return { exception, assetKind: 'script' };
	}

	return { exception };
};

/**
 * Captures an exception with optional structured metadata (extras & tags).
 *
 * The `extrasAndTags` object is attached both as Sentry extras (for search)
 * and selectively promoted to tags (for filtering/alerting).
 *
 * `options.fingerprint` replaces Sentry's default grouping. Asset-load failures
 * (stylesheets and lazy JS chunks) always share a fingerprint per asset kind
 * instead, and carry an `assetLoadError` tag, which the Slack alert filters on.
 */
export const logException = (
	exception: Error | unknown,
	extrasAndTags?: ExtrasAndTags,
	options?: LogOptions,
): void => {
	const reportable = toReportableException(exception);

	Sentry.withScope((scope) => {
		scope.setTag('name', DEFAULT_PRODUCT);

		if (extrasAndTags) {
			scope.setExtras(extrasAndTags);
			setTagsFromExtras(scope, extrasAndTags);
		}
		if (options?.fingerprint !== undefined) {
			scope.setFingerprint(options.fingerprint);
		}
		if (reportable.assetKind !== undefined) {
			scope.setFingerprint([ASSET_LOAD_ERROR_FINGERPRINT, reportable.assetKind]);
			scope.setTag('assetLoadError', reportable.assetKind);
		}
		Sentry.captureException(reportable.exception);
	});
};

/**
 * Captures an informational message with optional structured metadata.
 * `options.fingerprint` replaces Sentry's default grouping.
 */
export const logMessage = (
	message: string,
	extrasAndTags?: ExtrasAndTags,
	options?: LogOptions,
): void => {
	Sentry.withScope((scope) => {
		scope.setTag('name', DEFAULT_PRODUCT);

		if (extrasAndTags) {
			scope.setExtras(extrasAndTags);
			setTagsFromExtras(scope, extrasAndTags);
		}
		if (options?.fingerprint !== undefined) {
			scope.setFingerprint(options.fingerprint);
		}
		Sentry.captureMessage(message);
	});
};
